import { Injectable, Inject } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
//  Employing a auth static utility
import { AuthenticationUtility } from '../utils/authentication.utility';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.initMsalEvents();
  }

  private initMsalEvents() {
    this.authService.handleRedirectObservable().subscribe({
      next: () => {
        this.setLoginDisplay();
        this.getUserName();
      },
      error: (err) => {
        console.error('Error during login redirect handling:', err);
      }
    });

    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.ACCOUNT_ADDED ||
          msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
          this.getUserName();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.getUserName();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  getUserName(): string {
    const account = this.authService.instance.getActiveAccount();
    return account && account.name ? account.name : '';
  }

  getUserRoles(): string[] {
    const account = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()[0];
    if (account && account.idTokenClaims) {
      // Azure AD roles are usually in the 'roles' claim (array of strings)
      const roles = (account.idTokenClaims as any).roles || [];
      return roles;
    }
    return [];
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService
        .loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  //  #region role-based getters

  get isGateKeeper(): boolean {
    return AuthenticationUtility.isGateKeeper(this.getUserRoles());
  }

  get isDivisionGateKeeper(): boolean {
    return AuthenticationUtility.isDivisionGateKeeper(this.getUserRoles());
  }

  get isAreaGateKeeper(): boolean {
    return AuthenticationUtility.isAreaGateKeeper(this.getUserRoles());
  }

  get isRequestor(): boolean {
    return AuthenticationUtility.isRequestor(this.getUserRoles());
  }

  //  #endregion 
}
